// PreAdmissionForm.jsx
import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import Select from "react-select";
import { Cropper, RectangleStencil } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";


/**
 * Enhanced PreAdmissionForm
 * - prettier UI
 * - toast messages (react-toastify)
 * - step transition events (onBeforeStepChange / onAfterStepChange hooks)
 * - upload + preview thumbnails
 * - cropping modal for images (react-advanced-cropper)
 *
 * Keep your api calls the same (api.post / api.get).
 */

const PreAdmissionForm = ({ onBeforeStepChange, onAfterStepChange } = {}) => {
    // Steps: 1 => Data, 2 => Uploads, 3 => Payment
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState(null);
    const [applicationId, setApplicationId] = useState(null);
    const [applicationNo, setApplicationNo] = useState(null);

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [classes, setClasses] = useState([]);
    const [schools, setSchools] = useState([]);

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const status = query.get("status");
    const appId = query.get("app_id");

    const [finalData, setFinalData] = useState(null);

    const [form, setForm] = useState({
        name: "",
        dob: "",
        gender: "",
        aadhaar_no: "",
        mobile_no: "",
        class_applied: "",
        father_name: "",
        mother_name: "",
        state: "",
        district: "",
        block: "",
        locality: "",
        village: "",
        post: "",
        ps: "",
        pin: "",
        previous_class: "",
        previous_school_name: "",
        previous_marks: "",
        total_marks: "",
        previous_percent: "",
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const domain = params.get("domain");
        if (domain) {
            localStorage.setItem("school_domain", domain);
        }
    }, []);

    // files holds the raw File objects
    const [files, setFiles] = useState({
        photo: null,
        aadhaar_card: null,
        birth_certificate: null,
        marksheet: null,
    });

    // previews for thumbnails (object URLs)
    const [previews, setPreviews] = useState({
        photo: null,
        aadhaar_card: null,
        birth_certificate: null,
        marksheet: null,
    });

    // cropper modal state
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropTargetKey, setCropTargetKey] = useState(null); // which field are we cropping (photo, aadhaar_card)
    const [cropSrc, setCropSrc] = useState(null);
    const cropperRef = useRef(null);

     const domain = localStorage.getItem("school_domain");

    // Progress / tiny analytics events (can be replaced by user-provided callbacks)
    const triggerBeforeStep = (from, to) => {
        // call prop if provided
        try {
            onBeforeStepChange?.(from, to);
        } catch (e) { }
        // toast small notice
        toast.info(`Moving from step ${from} → ${to}`, { autoClose: 900, pauseOnHover: false });
        // custom console log for debugging / analytics
        console.log(`[PreAdmission] beforeStepChange: ${from} -> ${to}`);
    };

    const triggerAfterStep = (from, to) => {
        try {
            onAfterStepChange?.(from, to);
        } catch (e) { }
        toast.success(`Arrived at step ${to}`, { autoClose: 1200 });
        console.log(`[PreAdmission] afterStepChange: ${from} -> ${to}`);
    };

    // ---------- Fetch dropdown data ----------
    useEffect(() => {
        fetch("https://dgps.co.in/api/get-states")
            .then((res) => res.json())
            .then((data) => setStates(data.data || []))
            .catch((_) => setStates([]));
    }, []);

    useEffect(() => {
        if (form.state) {
            fetch(`https://dgps.co.in/api/get-cities/${form.state}`)
                .then((res) => res.json())
                .then((data) => setDistricts(data.data || []))
                .catch((_) => setDistricts([]));
        } else {
            setDistricts([]);
        }
    }, [form.state]);

    useEffect(() => {
        if (form.district) {
            fetch(`https://dgps.co.in/api/get-blocks/${form.district}`)
                .then((res) => res.json())
                .then((data) => setBlocks(data.data || []))
                .catch((_) => setBlocks([]));
        } else {
            setBlocks([]);
        }
    }, [form.district]);

    // useEffect(() => {
    //     fetch("https://dgps.co.in/api/get-class")
    //         .then((res) => res.json())
    //         .then((data) => setClasses(data.data || []))
    //         .catch((_) => setClasses([]));
    // }, []);

useEffect(() => {
  const fetchSidByDomain = async () => {
    try {
      // 1️⃣ Fetch school info by domain
      const res = await api.get(`/get-sid?domain=${domain}`);

      if (res.data && res.data.success && res.data.data) {
        const school = res.data.data;
        setSchools(school); // store whole school info if needed

        const schoolId = school.id;

        if (schoolId) {
          // 2️⃣ Then fetch classes using the school_id
          await fetchClasses(schoolId);
        } else {
          console.warn("No school ID found in response.");
        }
      } else {
        setSchools([]);
        console.warn("Unexpected response:", res.data);
      }
    } catch (error) {
      setSchools([]);
      console.error("Error fetching school by domain:", error);
    }
  };

  const fetchClasses = async (schoolId) => {
    try {
      const res = await api.get(`/get-class?school_id=${schoolId}`);

      if (res.data && res.data.success) {
        setClasses(res.data.data);
      } else {
        setClasses([]);
        console.warn("Unexpected response for classes:", res.data);
      }
    } catch (error) {
      setClasses([]);
      console.error("Error fetching classes:", error);
    }
  };

  fetchSidByDomain();
}, [domain]);


    // generate previews when files change
    useEffect(() => {
        const newPreviews = {};
        Object.keys(files).forEach((k) => {
            if (files[k]) {
                // for images only create object URL
                if (files[k].type && files[k].type.startsWith("image")) {
                    newPreviews[k] = URL.createObjectURL(files[k]);
                } else {
                    newPreviews[k] = null;
                }
            } else {
                newPreviews[k] = null;
            }
        });
        setPreviews((prev) => ({ ...prev, ...newPreviews }));

        // revoke URLs on cleanup
        return () => {
            Object.values(newPreviews).forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, [files]);

    // helpers
    const notify = (type, text) => {
        setMsg({ type, text });
        if (type === "success") toast.success(text);
        else if (type === "error") toast.error(text);
        else toast.info(text);
    };

    // --- Input Validation Helpers with Error ---
    const validateInput = (name, value) => {
        let error = "";
        let cleanValue = value;

        // --- Text-only fields ---
        if (
            [
                "name",
                "father_name",
                "mother_name",
                "locality",
                "village",
                "post",
                "ps",
                "previous_school_name",
            ].includes(name)
        ) {
            const regex = /^[A-Za-z\s]*$/;
            if (!regex.test(value)) error = "Only letters and spaces allowed";
            cleanValue = value.replace(/[^A-Za-z\s]/g, "").toUpperCase();
        }

        // --- Digits-only fields ---
        if (["mobile_no"].includes(name)) {
            cleanValue = value.replace(/\D/g, "").slice(0, 10);
            if (cleanValue.length > 0 && cleanValue.length < 10)
                error = "Must be 10 digits";
        }

        if (["aadhaar_no"].includes(name)) {
            cleanValue = value.replace(/\D/g, "").slice(0, 12);
            if (cleanValue.length > 0 && cleanValue.length < 12)
                error = "Must be 12 digits";
        }

        if (["pin"].includes(name)) {
            cleanValue = value.replace(/\D/g, "").slice(0, 6);
            if (cleanValue.length > 0 && cleanValue.length < 6)
                error = "Must be 6 digits";
        }

        if (["previous_marks"].includes(name)) {
            cleanValue = value.replace(/\D/g, "");
        }

        // --- Decimal allowed (like 64.59) ---
        if (["previous_percent"].includes(name)) {
            cleanValue = value.replace(/[^0-9.]/g, "");
            const parts = cleanValue.split(".");
            if (parts.length > 2) cleanValue = parts[0] + "." + parts.slice(1).join("");
            if (!/^\d*\.?\d*$/.test(cleanValue)) error = "Invalid percentage format";
        }

        return { cleanValue, error };
    };


    const handleChange = (name, value) => {
        // Validation per field type
        const { cleanValue, error } = validateInput(name, value);
        if (
            [
                "name",
                "father_name",
                "mother_name",
                "locality",
                "village",
                "post",
                "ps",
                "previous_school_name",
            ].includes(name)
        ) {
            value = onlyText(value);
        }

        if (["mobile_no"].includes(name)) {
            value = onlyDigits(value, 10);
        }

        if (["aadhaar_no"].includes(name)) {
            value = onlyDigits(value, 12);
        }

        if (["pin"].includes(name)) {
            value = onlyDigits(value, 6);
        }

        if (["previous_marks","total_marks"].includes(name)) {
            value = onlyDigits(value);
        }

        // Decimal field (allow 64.59 etc.)
        if (["previous_percent"].includes(name)) {
            value = onlyDecimal(value);
        }

        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: error }));
    };


    // --- Input Validation Helpers ---
    const onlyText = (value) => {
        // Allow letters, space only
        return value.replace(/[^A-Za-z\s]/g, "").toUpperCase();
    };

    const onlyDigits = (value, maxLength = null) => {
        let digits = value.replace(/\D/g, ""); // Remove non-digits
        if (maxLength) digits = digits.slice(0, maxLength);
        return digits;
    };


    const onlyDecimal = (value) => {
        // allow digits + single decimal point
        let clean = value.replace(/[^0-9.]/g, "");
        const parts = clean.split(".");
        if (parts.length > 2) {
            // remove extra dots
            clean = parts[0] + "." + parts.slice(1).join("");
        }
        return clean;
    };
    // ---------- Submit data ----------
    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setMsg(null);

        // Basic validation
        if (!form.name || !form.mobile_no || !form.father_name || !form.class_applied) {
            notify("error", "Please complete required fields (Name, Mobile, Father's name, Class).");
            return;
        }

        setLoading(true);
        try {
            let payload = { ...form };

            // ✅ Include school info manually (for extra safety)
            payload.school_id = localStorage.getItem("school_id") || null;
            payload.school_domain =
                localStorage.getItem("school_domain") ||
                new URLSearchParams(window.location.search).get("domain") ||
                window.location.hostname;

            let res;

            console.log('playload', payload);

            if (applicationId) {
                // ✅ UPDATE existing application
                payload.application_id = applicationId;
                res = await api.post("/application/update", payload);
            } else {
                // ✅ CREATE new application
                res = await api.post("/application/create", payload);
            }



            if (res.data?.status) {
                if (!applicationId) {
                    setApplicationId(res.data.application_id);
                    setApplicationNo(res.data.application_no);
                    notify("success", "Application created successfully!");
                } else {
                    notify("success", "Application updated successfully!");
                }

                // go to next step
                triggerBeforeStep(1, 2);
                setStep(2);
                triggerAfterStep(1, 2);
            } else {
                notify("error", res.data?.message || "Unable to save application.");
            }
        } catch (err) {
            notify("error", "Network or Server Error");
            console.error("Application submit error:", err);
        } finally {
            setLoading(false);
        }
    };


    // ---------- Payment ----------
    const handlePayment = async () => {
        if (!applicationId) {
            notify("error", "Application ID missing!");
            return;
        }
        try {
            setLoading(true);
            const res = await api.get(`/application/pay/${applicationId}`);
            if (res.data?.status && res.data.payment_url) {
                // before redirect event
                triggerBeforeStep(3, "payment_gateway_redirect");
                // redirect
                window.location.href = res.data.payment_url;
            } else {
                notify("error", res.data?.message || "Payment initiation failed");
            }
        } catch (err) {
            notify("error", "Payment initiation failed");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ---------- Uploads ----------
    const handleUpload = async () => {
        if (!applicationId) {
            notify("error", "Application ID missing!");
            return;
        }
        // ensure at least photo uploaded
        if (!files.photo) {
            notify("error", "Please upload the student's photo (and crop it if needed).");
            return;
        }

        const formData = new FormData();
        formData.append("application_id", applicationId);
        Object.keys(files).forEach((key) => {
            if (files[key]) formData.append(key, files[key]);
        });

        try {
            setLoading(true);
            const res = await api.post("/application/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.status) {
                notify("success", "Documents uploaded successfully!");
                triggerBeforeStep(2, 3);
                setStep(3);
                triggerAfterStep(2, 3);
            } else {
                notify("error", res.data?.message || "Upload failed");
            }
        } catch (err) {
            notify("error", "Upload failed");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (status === "success" && appId) {
            fetchFinalData(appId);
        }
    }, [status, appId]);

    const fetchFinalData = async (id) => {
        try {
            const res = await api.get(`/application/details/${id}`);
            if (res.data?.status) {
                setFinalData(res.data);
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            toast.error("Unable to fetch admission data");
        }
    };

    if (status === "success" && finalData) {
        return (
            <div>
                <ToastContainer position="top-right" />
                <AdmissionSummary data={finalData} />
            </div>
        );
    }


    // ---------- File change / Crop flow ----------
    const onFileSelect = (key, file) => {
        if (!file) return;
        // basic validation: size < 5MB
        const maxMB = 5;
        if (file.size / (1024 * 1024) > maxMB) {
            notify("error", `File too large. Max ${maxMB} MB allowed.`);
            return;
        }

        // If image and key==='photo' (or aadhaar_card), open cropper
        if (file.type && file.type.startsWith("image/") && (key === "photo" || key === "aadhaar_card")) {
            const src = URL.createObjectURL(file);
            setCropTargetKey(key);
            setCropSrc(src);
            setCropModalOpen(true);
        } else {
            setFiles((prev) => ({ ...prev, [key]: file }));
            notify("info", `${prettyKey(key)} selected`);
        }
    };

    const prettyKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (s) => s.toUpperCase());

    // Crop & Save
    const doCrop = () => {
        // get the canvas from cropper and convert to blob
        const cropper = cropperRef.current;
        if (!cropper) return;
        const canvas = cropper.getCanvas();
        if (!canvas) {
            notify("error", "No canvas available for cropping.");
            return;
        }
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    notify("error", "Crop failed.");
                    return;
                }
                // create a file
                const ext = blob.type || "image/jpeg";
                const filename = `${cropTargetKey || "photo"}.jpg`;
                const file = new File([blob], filename, { type: ext });
                // set file in state
                setFiles((prev) => ({ ...prev, [cropTargetKey]: file }));
                // set preview
                const url = URL.createObjectURL(blob);
                setPreviews((prev) => ({ ...prev, [cropTargetKey]: url }));

                // cleanup source object URL
                if (cropSrc) URL.revokeObjectURL(cropSrc);

                setCropModalOpen(false);
                setCropSrc(null);
                setCropTargetKey(null);
                notify("success", `${prettyKey(cropTargetKey)} cropped & saved.`);
            },
            "image/jpeg",
            0.9
        );
    };

    const cancelCrop = () => {
        if (cropSrc) URL.revokeObjectURL(cropSrc);
        setCropModalOpen(false);
        setCropSrc(null);
        setCropTargetKey(null);
    };

    // Step progress helper for header
    const stepTitles = {
        1: "Data Entry",
        2: "Upload Documents",
        3: "Payment",
    };

    // small progress indicator %
    const stepPercent = () => {
        if (step === 1) return 33;
        if (step === 2) return 66;
        return 100;
    };
    const UploadBox = ({ label, fileKey, preview, onFileSelect, note }) => {
        const handleClick = () => {
            document.getElementById(`file-${fileKey}`).click();
        };

        return (
            <div style={uploadBoxPro} onClick={handleClick} className="uploadBoxPro">
                <div style={uploadTop}>
                    <span style={uploadTitle}>{label}</span>
                </div>

                <div style={uploadInnerPro}>
                    {preview ? (
                        <img
                            src={preview}
                            alt={label}
                            style={{
                                width: "100%",
                                height: 150,
                                objectFit: "cover",
                                borderRadius: 8,
                                transition: "transform 0.3s ease",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            }}
                        />
                    ) : (
                        <div style={uploadPlaceholderPro}>
                            <i
                                className="fa fa-upload"
                                style={{ fontSize: 24, color: "#94a3b8", marginBottom: 6 }}
                            ></i>
                            <div style={{ color: "#64748b", fontSize: 13 }}>
                                Click to upload
                            </div>
                            <small style={{ color: "#9ca3af", fontSize: 11 }}>
                                JPG / PDF / PNG
                            </small>
                        </div>
                    )}
                </div>

                <input
                    id={`file-${fileKey}`}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => onFileSelect(fileKey, e.target.files[0])}
                />

                {note && (
                    <div style={uploadNote}>
                        {note}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={containerStyle}>
            <ToastContainer position="top-right" />
            <div style={cardStyle}>
                <div style={headerRow}>
                    <div>
                        <h2 style={titleStyle}>🎓 Pre-Admission</h2>
                        <div style={{ color: "#6b7280" }}>{stepTitles[step]}</div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                        <div style={smallMeta}>Step {step} / 3</div>
                        <div style={progressBarOuter}>
                            <div style={{ ...progressBarInner, width: `${stepPercent()}%` }} />
                        </div>
                    </div>
                </div>

                <hr style={{ border: "none", borderTop: "1px solid #eef2f7", margin: "16px 0 20px" }} />

                {msg && (
                    <div
                        style={{
                            color: msg.type === "error" ? "#c53030" : "#166534",
                            marginBottom: 12,
                        }}
                    >
                        {msg.text}
                    </div>
                )}

                {/* Step 1 */}
                {step === 1 && (
                    <form onSubmit={handleSubmit}>
                        <div style={formGrid}>
                            <FormGroup label="Student Name*">
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    style={{
                                        ...fullInput,
                                        borderColor: errors.name ? "#dc2626" : "#e6eef7", // red border if error
                                    }}
                                    required
                                />
                                {errors.name && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.name}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Date of Birth">
                                <input
                                    type="date"
                                    value={form.dob}
                                    onChange={(e) => handleChange("dob", e.target.value)}
                                    style={fullInput}
                                />
                            </FormGroup>

                            <FormGroup label="Gender">
                                <select value={form.gender} onChange={(e) => handleChange("gender", e.target.value)} style={fullInput}>
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </FormGroup>

                            <FormGroup label="Mobile No*">
                                <input type="text" value={form.mobile_no} onChange={(e) => handleChange("mobile_no", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.mobile_no ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.mobile_no && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.mobile_no}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Aadhaar No">
                                <input type="text" value={form.aadhaar_no} onChange={(e) => handleChange("aadhaar_no", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.aadhaar_no ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.aadhaar_no && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.aadhaar_no}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Class Applied*">
                                <Select
                                    options={classes.map((c) => ({ value: c.id, label: c.class_name }))}
                                    value={
                                        classes.find((c) => c.id === form.class_applied)
                                            ? { value: form.class_applied, label: classes.find((c) => c.id === form.class_applied).class_name }
                                            : null
                                    }
                                    onChange={(selected) => handleChange("class_applied", selected ? selected.value : "")}
                                    placeholder="Select class..."
                                    isSearchable
                                    isClearable
                                />
                            </FormGroup>

                            <FormGroup label="Father's Name*">
                                <input type="text" value={form.father_name} onChange={(e) => handleChange("father_name", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.father_name ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.father_name && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.father_name}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Mother's Name">
                                <input type="text" value={form.mother_name} onChange={(e) => handleChange("mother_name", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.mother_name ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.mother_name && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.mother_name}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="State">
                                <Select
                                    options={states.map((s) => ({ value: s.id, label: s.name }))}
                                    value={states.find((s) => s.id === form.state) ? { value: form.state, label: states.find((s) => s.id === form.state)?.name } : null}
                                    onChange={(selected) => handleChange("state", selected ? selected.value : "")}
                                    placeholder="Select state..."
                                    isClearable
                                    isSearchable
                                />
                            </FormGroup>

                            <FormGroup label="District">
                                <Select
                                    options={districts.map((d) => ({ value: d.id, label: d.name }))}
                                    value={districts.find((d) => d.id === form.district) ? { value: form.district, label: districts.find((d) => d.id === form.district)?.name } : null}
                                    onChange={(selected) => handleChange("district", selected ? selected.value : "")}
                                    placeholder="Select district..."
                                    isClearable
                                    isSearchable
                                />
                            </FormGroup>

                            <FormGroup label="Block">
                                <Select
                                    options={blocks.map((b) => ({ value: b.id, label: b.name }))}
                                    value={blocks.find((b) => b.id === form.block) ? { value: form.block, label: blocks.find((b) => b.id === form.block)?.name } : null}
                                    onChange={(selected) => handleChange("block", selected ? selected.value : "")}
                                    placeholder="Select block..."
                                    isClearable
                                    isSearchable
                                />
                            </FormGroup>

                            <FormGroup label="Locality">
                                <input type="text" value={form.locality} onChange={(e) => handleChange("locality", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.locality ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.locality && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.locality}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Village">
                                <input type="text" value={form.village} onChange={(e) => handleChange("village", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.village ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.village && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.village}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Post Office">
                                <input type="text" value={form.post} onChange={(e) => handleChange("post", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.post ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.post && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.post}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Police Station">
                                <input type="text" value={form.ps} onChange={(e) => handleChange("ps", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.ps ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.ps && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.ps}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="PIN Code">
                                <input type="text" value={form.pin} onChange={(e) => handleChange("pin", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.pin ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.pin && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.pin}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Previous Class Name">
                                <Select
                                    options={classes.map((c) => ({ value: c.id, label: c.class_name }))}
                                    value={
                                        classes.find((c) => c.id === form.previous_class)
                                            ? { value: form.previous_class, label: classes.find((c) => c.id === form.previous_class).class_name }
                                            : null
                                    }
                                    onChange={(selected) => handleChange("previous_class", selected ? selected.value : "")}
                                    placeholder="Select class..."
                                    isSearchable
                                    isClearable
                                />
                            </FormGroup>

                            <FormGroup label="Previous School Name">
                                <input type="text" value={form.previous_school_name} onChange={(e) => handleChange("previous_school_name", e.target.value)} style={{
                                    ...fullInput,
                                    borderColor: errors.previous_school_name ? "#dc2626" : "#e6eef7", // red border if error
                                }} />     {errors.previous_school_name && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>{errors.previous_school_name}</small>
                                )}
                            </FormGroup>

                            <FormGroup label="Previous Marks (Obtained)">
                                <input
                                    type="text"
                                    value={form.previous_marks}
                                    onChange={(e) => {
                                        const obtained = e.target.value;
                                        handleChange("previous_marks", obtained);

                                        // auto calculate percentage if total_marks exists
                                        if (form.total_marks && obtained) {
                                            const percent = ((obtained / form.total_marks) * 100).toFixed(2);
                                            handleChange("previous_percent", percent);
                                        } else {
                                            handleChange("previous_percent", "");
                                        }
                                    }}
                                    style={{
                                        ...fullInput,
                                        borderColor: errors.previous_marks ? "#dc2626" : "#e6eef7",
                                    }}
                                />
                                {errors.previous_marks && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>
                                        {errors.previous_marks}
                                    </small>
                                )}
                            </FormGroup>

                            <FormGroup label="Total Marks">
                                <input
                                    type="text"
                                    value={form.total_marks}
                                    onChange={(e) => {
                                        const total = e.target.value;
                                        handleChange("total_marks", total);

                                        // auto calculate percentage if obtained exists
                                        if (form.previous_marks && total) {
                                            const percent = ((form.previous_marks / total) * 100).toFixed(2);
                                            handleChange("previous_percent", percent);
                                        } else {
                                            handleChange("previous_percent", "");
                                        }
                                    }}
                                    style={{
                                        ...fullInput,
                                        borderColor: errors.total_marks ? "#dc2626" : "#e6eef7",
                                    }}
                                />
                                {errors.total_marks && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>
                                        {errors.total_marks}
                                    </small>
                                )}
                            </FormGroup>

                            <FormGroup label="Percentage">
                                <input
                                    type="text"
                                    value={form.previous_percent}
                                    readOnly
                                    style={{
                                        ...fullInput,
                                        backgroundColor: "#f9fafb",
                                        color: "#374151",
                                        cursor: "not-allowed",
                                        borderColor: errors.previous_percent ? "#dc2626" : "#e6eef7",
                                    }}
                                />
                                {errors.previous_percent && (
                                    <small style={{ color: "#dc2626", fontSize: 12 }}>
                                        {errors.previous_percent}
                                    </small>
                                )}
                            </FormGroup>

                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                            <div />
                            <div>
                                <button
                                    type="submit"
                                    style={{ ...buttonStyle, minWidth: 160 }}
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save & Proceed →"}
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {/* Step 2: Uploads */}
                {step === 2 && (
                    <div>
                        <h3 style={{ marginTop: 0, marginBottom: 20, color: "#0b6fb5" }}>
                            📎 Upload Documents
                        </h3>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 24,
                            }}
                        >
                            {/* -------- Left Column -------- */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                {/* Photo Upload */}
                                <UploadBox
                                    label="Student Photo (JPG only)"
                                    fileKey="photo"
                                    preview={previews.photo}
                                    onFileSelect={onFileSelect}
                                    note="Upload a clear passport-size photo"
                                />

                                {/* Aadhaar Upload */}
                                <UploadBox
                                    label="Aadhaar Card (Image / PDF)"
                                    fileKey="aadhaar_card"
                                    preview={previews.aadhaar_card}
                                    onFileSelect={onFileSelect}
                                    note="Upload front side of Aadhaar"
                                />
                            </div>

                            {/* -------- Right Column -------- */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                {/* Birth Certificate */}
                                <UploadBox
                                    label="Birth Certificate (Image / PDF)"
                                    fileKey="birth_certificate"
                                    preview={previews.birth_certificate}
                                    onFileSelect={onFileSelect}
                                    note="Upload valid birth certificate"
                                />

                                {/* Marksheet */}
                                <UploadBox
                                    label="Previous Marksheet (if any)"
                                    fileKey="marksheet"
                                    preview={previews.marksheet}
                                    onFileSelect={onFileSelect}
                                    note="Optional - upload last exam marksheet"
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: 28,
                            }}
                        >
                            <button
                                onClick={() => {
                                    triggerBeforeStep(2, 1);
                                    setStep(1);
                                    triggerAfterStep(2, 1);
                                }}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: "#e5e7eb",
                                    color: "#111",
                                    minWidth: 120,
                                }}
                                disabled={loading}
                            >
                                ← Back
                            </button>

                            <button
                                onClick={handleUpload}
                                style={{ ...buttonStyle, minWidth: 180 }}
                                disabled={loading}
                            >
                                {loading ? "Uploading..." : "Upload & Continue →"}
                            </button>
                        </div>
                    </div>
                )}


                {/* Step 3: Payment */}
                {step === 3 && (
                    <div style={{ textAlign: "center" }}>
                        <h3 style={{ marginBottom: 6 }}>Ready for Payment</h3>
                        <p style={{ color: "#6b7280" }}>
                            Application ID: <b>{applicationId}</b>
                        </p>
                        <p style={{ color: "#6b7280" }}>
                            Application No: <b>{applicationNo}</b>
                        </p>

                        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16 }}>
                            <button
                                onClick={() => {
                                    // allow editing uploads again
                                    triggerBeforeStep(3, 2);
                                    setStep(2);
                                    triggerAfterStep(3, 2);
                                }}
                                style={{ ...buttonStyle, backgroundColor: "#e5e7eb", color: "#111", minWidth: 140 }}
                                disabled={loading}
                            >
                                ← Edit Uploads
                            </button>

                            <button onClick={handlePayment} style={{ ...buttonStyle, minWidth: 160 }} disabled={loading}>
                                {loading ? "Redirecting..." : "Pay Application Fee"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Crop modal overlay */}
            {cropModalOpen && (
                <div style={cropModalStyle}>
                    <div style={cropModalInner}>
                        <h4 style={{ marginTop: 0 }}>Crop {prettyKey(cropTargetKey)}</h4>
                        <div style={{ display: "flex", gap: 16 }}>
                            <div style={{ width: 420, height: 360, background: "#f8fafc" }}>
                                {cropSrc ? (
                                    <Cropper
                                        src={cropSrc}
                                        ref={cropperRef}
                                        stencilComponent={RectangleStencil}
                                        stencilSize={{ width: 300, height: 380 }}
                                        className="cropper"
                                    // you can add more props for zoom/rotation if you wish
                                    />
                                ) : (
                                    <div style={{ padding: 20 }}>Loading image...</div>
                                )}
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <small style={{ color: "#6b7280" }}>
                                        Use the crop box to frame the student's face. Click Save to finalize.
                                    </small>
                                </div>

                                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                                    <button onClick={doCrop} style={buttonStyle}>
                                        Save Crop
                                    </button>
                                    <button onClick={cancelCrop} style={{ ...buttonStyle, backgroundColor: "#e5e7eb", color: "#111" }}>
                                        Cancel
                                    </button>
                                </div>

                                <div style={{ marginTop: 18 }}>
                                    <small style={{ color: "#64748b" }}>
                                        Tip: For best results, use a clear photo with the student's face centered.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ----------------- Styles ----------------- */
const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
    padding: "40px",
};

const cardStyle = {
    backgroundColor: "#fff",
    padding: "28px",
    borderRadius: "12px",
    boxShadow: "0 6px 18px rgba(2,6,23,0.08)",
    maxWidth: "1000px",
    width: "100%",
};

const headerRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

const smallMeta = {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 8,
};

const progressBarOuter = {
    width: 160,
    height: 8,
    backgroundColor: "#eef2f7",
    borderRadius: 6,
    overflow: "hidden",
};

const progressBarInner = {
    height: "100%",
    backgroundColor: "#0ea5e9",
    borderRadius: 6,
    transition: "width 400ms ease",
};

const titleStyle = {
    color: "#0b6fb5",
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: 6,
};

const formGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
};

const fullInput = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e6eef7",
    fontSize: "14px",
    boxSizing: "border-box",
    background: "#fbfdff",
};

const buttonStyle = {
    backgroundColor: "#0b6fb5",
    color: "#fff",
    padding: "10px 14px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
};

const FormGroup = ({ label, children }) => (
    <div style={{ marginBottom: 6 }}>
        <label style={{ display: "block", marginBottom: 8, color: "#0f172a", fontSize: 14 }}>{label}</label>
        {children}
    </div>
);

const cropModalStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1200,
};

const cropModalInner = {
    width: "900px",
    maxWidth: "95%",
    background: "#fff",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 8px 30px rgba(2,6,23,0.25)",
};
const uploadBoxPro = {
    border: "2px dashed #cbd5e1",
    borderRadius: 12,
    background: "#f9fafb",
    transition: "0.25s ease",
    cursor: "pointer",
    padding: "10px 14px 14px",
    textAlign: "center",
    boxShadow: "inset 0 0 6px rgba(0,0,0,0.02)",
    height: 240,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
};

const uploadTop = {
    textAlign: "left",
    fontWeight: 600,
    color: "#0b6fb5",
    fontSize: 13,
    marginBottom: 6,
};

const uploadTitle = {
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: 4,
    display: "inline-block",
};

const uploadInnerPro = {
    background: "#f8fafc",
    borderRadius: 8,
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
};

const uploadPlaceholderPro = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    width: "100%",
    height: "100%",
    background: "#f1f5f9",
    borderRadius: 8,
    border: "1px dashed #d1d5db",
};

const uploadNote = {
    marginTop: 6,
    fontSize: 11,
    color: "#6b7280",
};

export default PreAdmissionForm;

const AdmissionSummary = ({ data }) => {
    const { application, school } = data;

    return (
        <div style={pageContainer}>

            <div style={formCard} id="admission-form">
                {/* ---------- HEADER ---------- */}
                <div style={headerSection}>
                    <div style={headerFlex}>
                        <div style={logoBox}>
                            <img
                                src={`https://dgps.co.in${school.logo}`}
                                alt="School Logo"
                                style={schoolLogo}
                            />
                        </div>
                        <div style={schoolDetailsBox}>
                            <h2 style={schoolName}>{school.name}</h2>
                            <p style={schoolAddress}>{school.address}</p>
                            <p style={schoolContact}>
                                📞 {school.phone} &nbsp;&nbsp; ✉️ {school.email}
                            </p>
                        </div>
                    </div>
                </div>

                <h3 style={formTitle}>PRE-ADMISSION FORM</h3>
                <hr style={divider} />

                {/* ---------- STUDENT DETAILS (with photo) ---------- */}
                <Section title="Student Details">
                    <div style={studentDetailGrid}>
                        <div style={{ flex: 1 }}>
                            <InfoRow label="Application No" value={application.application_no} />
                            <InfoRow label="Student Name" value={application.name} />
                            <InfoRow label="Date of Birth" value={application.dob} />
                            <InfoRow label="Gender" value={application.gender} />
                            <InfoRow label="Aadhaar No" value={application.aadhaar_no} />
                            <InfoRow label="Mobile No" value={application.mobile_no} />
                            <InfoRow
                                label="Address"
                                value={`${application.village}, ${application.post}, ${application.block_name}, ${application.district_name}, ${application.state_name}, PIN - ${application.pin}`}
                            />
                        </div>

                        <div style={photoBox}>
                            {application.photo ? (
                                <img
                                    src={`https://dgps.co.in/${application.photo}`}
                                    alt="Student"
                                    style={studentPhoto}
                                />
                            ) : (
                                <div style={photoPlaceholder}>Photo</div>
                            )}
                        </div>
                    </div>
                </Section>

                {/* ---------- GUARDIAN DETAILS ---------- */}
                <Section title="Guardian Details">
                    <InfoRow label="Father’s Name" value={application.father_name} />
                    <InfoRow label="Mother’s Name" value={application.mother_name || "-"} />
                </Section>

                {/* ---------- ACADEMIC DETAILS ---------- */}
                <Section title="Academic Details">
                    <InfoRow label="Class Applied" value={application.class_applied_name} />
                    <InfoRow
                        label="Previous Class"
                        value={application.previous_class_name || "-"}
                    />
                    <InfoRow
                        label="Previous School"
                        value={application.previous_school_name || "-"}
                    />
                    <InfoRow
                        label="Previous Marks"
                        value={
                            application.previous_marks
                                ? `${application.previous_marks} (${application.previous_percent}%)`
                                : "-"
                        }
                    />
                </Section>

                {/* ---------- PAYMENT DETAILS ---------- */}
                <Section title="Payment Details">
                    <InfoRow label="Transaction ID" value={application.transaction_id} />
                    <InfoRow label="Payment Method" value={application.payment_method} />
                    <InfoRow label="Amount Paid" value={`₹${application.amount}`} />
                    <InfoRow
                        label="Payment Status"
                        value={application.payment_status?.toUpperCase()}
                    />
                    <InfoRow
                        label="Submission Date"
                        value={new Date(application.created_at).toLocaleString()}
                    />
                </Section>

                {/* ---------- SIGNATURE ---------- */}
                <div style={signatureBox}>
                    <div style={sigColumn}>
                        <hr style={sigLine} />
                        <p style={sigLabel}>Guardian's Signature</p>
                    </div>
                    <div style={sigColumn}>
                        <hr style={sigLine} />
                        <p style={sigLabel}>Authorized Signature</p>
                    </div>
                </div>

                {/* ---------- PRINT BUTTON ---------- */}
                <div className="print-hidden" style={{ textAlign: "center", marginTop: 25 }}>
                    <button onClick={() => window.print()} style={printButton}>
                        🖨️ Print / Download Admission Form
                    </button>
                </div>

                {/* ---------- PRINT CSS ---------- */}
                <style>{`
                    @media print {
                        /* Hide print buttons */
                        .print-hidden, #print-btn {
                        display: none !important;
                        }

                        /* Clean background */
                        body {
                        background: #fff !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        box-shadow: #fff !important;
                        }

                        /* Remove shadow & background tint from form */
                        #admission-form {
                        background: #fff !important;
                        box-shadow: #fff !important;
                        border: none !important;
                        padding: 20px 40px !important;
                        }

                        /* Fix section borders in print */
                        .sectionContent, div[style*='border'] {
                       
                        }
                    }
                `}</style>

            </div>
        </div>
    );
};

/* ---------------- Reusable Components ---------------- */
const Section = ({ title, children }) => (
    <div style={sectionBox}>
        <h4 style={sectionTitle}>{title}</h4>
        <div style={sectionContent}>{children}</div>
    </div>
);

const InfoRow = ({ label, value }) => (
    <div style={infoRow}>
        <div style={infoLabel}>{label}</div>
        <div style={infoValue}>{value || "-"}</div>
    </div>
);

/* -------------------- A4 Layout Styles -------------------- */
const pageContainer = {
    background: "#f2f4f7",
    display: "flex",
    justifyContent: "center",
    padding: "40px 0",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
};

const formCard = {
    background: "#fff",
    width: "210mm", // A4 width
    minHeight: "297mm", // A4 height
    padding: "20mm",
    boxSizing: "border-box",
    borderRadius: 12,
    color: "#111827",
};

const headerSection = { marginBottom: 20 };
const headerFlex = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
};

const logoBox = { flexShrink: 0 };
const schoolLogo = { width: 100, borderRadius: 8, border: "2px solid #e5e7eb" };
const schoolDetailsBox = { textAlign: "center", lineHeight: 1.4 };
const schoolName = {
    margin: 0,
    color: "#0b6fb5",
    fontWeight: 700,
    fontSize: 22,
    textTransform: "uppercase",
};
const schoolAddress = { margin: "4px 0", fontSize: 14, color: "#444" };
const schoolContact = { margin: 0, fontSize: 14, color: "#555" };
const formTitle = {
    textAlign: "center",
    fontSize: 18,
    color: "#111827",
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
};
const divider = { borderTop: "2px solid #0b6fb5", margin: "10px 0 25px" };

const sectionBox = { marginBottom: 20 };
const sectionTitle = {
    background: "#0b6fb5",
    color: "#fff",
    padding: "5px 10px",
    borderRadius: 4,
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 8,
};
const sectionContent = {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "10px 20px",
    background: "#f9fafb",
};
const infoRow = {
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: "1px solid #e5e7eb",
    columnGap: "18px",
};
const infoLabel = {
    fontWeight: 600,
    color: "#1e293b",
    textAlign: "left",
    fontSize: 14,
};
const infoValue = {
    color: "#334155",
    textAlign: "left",
    fontSize: 14,
    lineHeight: 1.5,
};

const studentDetailGrid = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
};
const photoBox = { marginLeft: 30, textAlign: "center" };
const studentPhoto = {
    width: 110,
    height: 130,
    objectFit: "cover",
    border: "2px solid #ccc",
    borderRadius: 8,
};
const photoPlaceholder = {
    width: 110,
    height: 130,
    border: "2px dashed #ccc",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#aaa",
    fontSize: 13,
};

const signatureBox = { display: "flex", justifyContent: "space-between", marginTop: 40 };
const sigColumn = { width: "40%", textAlign: "center" };
const sigLine = { borderTop: "1px solid #111", marginBottom: 6 };
const sigLabel = { fontSize: 13, color: "#111", margin: 0 };

const printButton = {
    backgroundColor: "#0b6fb5",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 600,
    marginTop: 30,
};
